import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { listarPlanos, listarRotasPlano } from './api';
import RouteMap from './RouteMap';
import RouteItinerary from './RouteItinerary';
import './RotogramaModule.css';

interface Checkpoint {
  id?: number;
  ordem: number;
  latitude: number;
  longitude: number;
  pickup_in: string | null;
  pickup_out: string | null;
  supplier: string | null;
  city: string | null;
  gate_in: string | null;
  dados_completos: any;
  tipo?: 'origem' | 'destino' | 'pedagio' | 'pausa' | 'fim_jornada';
  nome?: string;
  duracao_parada?: number;
  distancia_ate_proximo?: number;
  tempo_ate_proximo?: number;
  coordenadas?: [number, number]; // Adicionado para suportar conversão
}

interface Rota {
  codigo_rota: string;
  tipo_veiculo: string;
  total_checkpoints: number;
  checkpoints: Checkpoint[];
  origem: string;
  destino: string;
  distancia_total_km: number;
}

interface AnaliseResponse {
  success: boolean;
  arquivo: string;
  total_rotas: number;
  total_checkpoints: number;
  colunas_identificadas: any;
  headers: string[];
  rotas: Rota[];
}

const RotogramaModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'upload' | 'rotas' | 'historico'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [analiseResultado, setAnaliseResultado] = useState<AnaliseResponse | null>(null);
  const [rotasSalvas, setRotasSalvas] = useState<any[]>([]);
  const [planoSelecionado, setPlanoSelecionado] = useState<number | null>(null);
  const [planos, setPlanos] = useState<any[]>([]);
  const [rotogramaSelecionado, setRotogramaSelecionado] = useState<any | null>(null);
  const [visualizandoRotograma, setVisualizandoRotograma] = useState(false);
  
  // Estados para edição de rota com mapa
  const [editandoRota, setEditandoRota] = useState(false);
  const [rotaEmEdicao, setRotaEmEdicao] = useState<any | null>(null);
  const [checkpointsEditados, setCheckpointsEditados] = useState<Checkpoint[]>([]);
  const [rotaCalculada, setRotaCalculada] = useState<any | null>(null);
  const [calculandoRota, setCalculandoRota] = useState(false);
    // Buscar planos e rotas salvas ao abrir a aba "Rotas Analisadas"
    useEffect(() => {
      if (activeTab === 'rotas') {
        setLoading(true);
        listarPlanos()
          .then((planos) => {
            setPlanos(planos);
            if (planos.length > 0) {
              setPlanoSelecionado(planos[0].id);
              return listarRotasPlano(planos[0].id);
            } else {
              setRotasSalvas([]);
              setLoading(false);
              return null;
            }
          })
          .then((rotas) => {
            if (rotas) setRotasSalvas(rotas);
            setLoading(false);
          })
          .catch(() => {
            setRotasSalvas([]);
            setLoading(false);
          });
      }
    }, [activeTab]);

    // Trocar plano selecionado
    const handlePlanoChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const id = Number(e.target.value);
      setPlanoSelecionado(id);
      setLoading(true);
      setVisualizandoRotograma(false);
      setRotogramaSelecionado(null);
      try {
        const rotas = await listarRotasPlano(id);
        setRotasSalvas(rotas);
      } catch {
        setRotasSalvas([]);
      }
      setLoading(false);
    };

  const handleVisualizarRotograma = async (rotaId: number) => {
    setLoading(true);
    try {
      // Forçar recálculo da rota para garantir pontos detalhados atualizados
      await axios.post(`${API_URL}/api/rotograma/rotas/${rotaId}/calcular`, {});
      // Buscar rotograma atualizado
      const response = await axios.get(`${API_URL}/api/rotograma/rotas/${rotaId}/rotograma`);
      setRotogramaSelecionado(response.data);
      setVisualizandoRotograma(true);
    } catch (error) {
      console.error('Erro ao buscar rotograma:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar rotograma' });
    } finally {
      setLoading(false);
    }
  };

  const handleVoltarParaLista = () => {
    setVisualizandoRotograma(false);
    setRotogramaSelecionado(null);
  };

  const handleExportarPDF = () => {
    window.print();
  };

  // Funções para edição de rota com mapa
  const handleEditarRota = async (rotaId: number) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/rotograma/rotas/${rotaId}`);
      const rota = response.data;
      
      // Converter checkpoints para formato editável
      const checkpointsFormatados = rota.checkpoints.map((cp: any, index: number) => ({
        ...cp,
        tipo: index === 0 ? 'origem' : (index === rota.checkpoints.length - 1 ? 'destino' : 'parada'),
        nome: cp.city || cp.supplier || `Checkpoint ${index + 1}`
      }));
      
      setRotaEmEdicao(rota);
      setCheckpointsEditados(checkpointsFormatados);
      setRotaCalculada(rota.rota_calculada ? JSON.parse(rota.rota_calculada) : null);
      setEditandoRota(true);
      setVisualizandoRotograma(false);
    } catch (error) {
      console.error('Erro ao carregar rota para edição:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar rota' });
    } finally {
      setLoading(false);
    }
  };

  const handleCalcularRota = async () => {
    if (!rotaEmEdicao || checkpointsEditados.length < 2) return;
    
    setCalculandoRota(true);
    try {
      const response = await axios.post(`${API_URL}/api/rotograma/rotas/${rotaEmEdicao.id}/calcular`, {
        checkpoints: checkpointsEditados
      });
      
      console.log('[ROTOGRAMA] Resposta do cálculo:', response.data);
      setRotaCalculada(response.data.rota_calculada || response.data);
      
      const rotaData = response.data.rota_calculada || response.data;
      setMessage({ 
        type: 'success', 
        text: `Rota calculada! Distância: ${rotaData.distancia_total_km}km, Tempo: ${Math.floor(rotaData.tempo_total_minutos / 60)}h ${rotaData.tempo_total_minutos % 60}min` 
      });
    } catch (error: any) {
      console.error('Erro ao calcular rota:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Erro ao calcular rota' 
      });
    } finally {
      setCalculandoRota(false);
    }
  };

  const handleSalvarRotaEditada = async () => {
    if (!rotaEmEdicao || !rotaCalculada) return;
    
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/rotograma/rotas/${rotaEmEdicao.id}/rota-calculada`, {
        checkpoints: checkpointsEditados,
        geometria: rotaCalculada.geometry,
        horario_inicio: new Date().toISOString()
      });
      
      setMessage({ type: 'success', text: 'Rota atualizada com sucesso!' });
      setEditandoRota(false);
      setRotaEmEdicao(null);
      setCheckpointsEditados([]);
      setRotaCalculada(null);
      
      // Voltar para lista sem recarregar (evita erro 404)
      setActiveTab('rotas');
    } catch (error: any) {
      console.error('Erro ao salvar rota editada:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Erro ao salvar rota' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelarEdicao = () => {
    setEditandoRota(false);
    setRotaEmEdicao(null);
    setCheckpointsEditados([]);
    setRotaCalculada(null);
  };

  const [rotasSelecionadas, setRotasSelecionadas] = useState<Set<string>>(new Set());
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Handlers para drag and drop
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Tipo de arquivo não suportado. Use Excel (.xlsx, .xls) ou CSV.' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Arquivo muito grande. O tamanho máximo é 10MB.' });
      return;
    }

    setSelectedFile(file);
    setMessage(null);
    setAnaliseResultado(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setAnaliseResultado(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAnalisarArquivo = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setMessage(null);

    const formData = new FormData();
    formData.append('arquivo', selectedFile);

    try {
      const response = await axios.post<AnaliseResponse>(
        `${API_URL}/api/rotograma/analisar`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setAnaliseResultado(response.data);
      setMessage({
        type: 'success',
        text: `Análise concluída! ${response.data.total_rotas} rotas identificadas com ${response.data.total_checkpoints} checkpoints.`
      });
      // Permanecer na aba de importação para mostrar rotas e permitir seleção
    } catch (error: any) {
      console.error('Erro ao analisar arquivo:', error);
      console.error('Resposta do servidor:', error.response?.data);
      
      let errorMessage = 'Erro ao analisar arquivo. Verifique o formato e tente novamente.';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.error) {
          errorMessage = data.error;
          
          // Adicionar informações extras se houver
          if (data.colunasFaltando) {
            errorMessage += ` - Colunas faltando: ${data.colunasFaltando.join(', ')}`;
          }
          if (data.headersEncontrados) {
            console.log('Headers encontrados no arquivo:', data.headersEncontrados);
            errorMessage += ` - Headers encontrados: ${data.headersEncontrados.join(', ')}`;
          }
        }
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRota = (codigoRota: string) => {
    const novasRotas = new Set(rotasSelecionadas);
    if (novasRotas.has(codigoRota)) {
      novasRotas.delete(codigoRota);
    } else {
      novasRotas.add(codigoRota);
    }
    setRotasSelecionadas(novasRotas);
  };

  const handleSelecionarTodas = () => {
    if (!analiseResultado) return;
    
    if (rotasSelecionadas.size === analiseResultado.rotas.length) {
      setRotasSelecionadas(new Set());
    } else {
      setRotasSelecionadas(new Set(analiseResultado.rotas.map(r => r.codigo_rota)));
    }
  };

  const handleSalvarRotas = async () => {
    if (!analiseResultado || rotasSelecionadas.size === 0) return;

    setLoading(true);
    setMessage(null);

    const rotasParaSalvar = analiseResultado.rotas.filter(r => 
      rotasSelecionadas.has(r.codigo_rota)
    );

    try {
      const response = await axios.post(`${API_URL}/api/rotograma/salvar`, {
        nome_plano: `Plano ${analiseResultado.arquivo} - ${new Date().toLocaleDateString()}`,
        arquivo_original: analiseResultado.arquivo,
        rotas: rotasParaSalvar
      });

      setMessage({
        type: 'success',
        text: `${rotasParaSalvar.length} rotas salvas com sucesso!`
      });
      
      // Limpar seleção e análise
      setRotasSelecionadas(new Set());
      setAnaliseResultado(null);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Mudar para aba de rotas salvas
      setActiveTab('rotas');
    } catch (error: any) {
      console.error('Erro ao salvar rotas:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Erro ao salvar rotas. Tente novamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderUploadTab = () => (
    <div className="upload-section">
      <h3>Importar Plano de Transporte</h3>
      
      {message && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div
        className={`upload-area ${dragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="upload-icon">📁</div>
        <p className="upload-text">
          Arraste e solte o arquivo aqui ou clique para selecionar
        </p>
        <p className="upload-hint">
          Formatos aceitos: Excel (.xlsx, .xls) ou CSV - Tamanho máximo: 10MB
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="file-input"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileInputChange}
      />

      {selectedFile && (
        <div className="selected-file">
          <div className="file-info">
            <span>📄</span>
            <span>{selectedFile.name}</span>
            <span style={{ color: '#999', fontSize: '0.9rem' }}>
              ({(selectedFile.size / 1024).toFixed(2)} KB)
            </span>
          </div>
          <button className="remove-file" onClick={handleRemoveFile}>
            ✕
          </button>
        </div>
      )}

      <div className="upload-actions">
        <button
          className="btn btn-primary"
          onClick={handleAnalisarArquivo}
          disabled={!selectedFile || loading}
        >
          {loading ? 'Analisando...' : 'Analisar Arquivo'}
        </button>
        <button
          className="btn btn-secondary"
          onClick={handleRemoveFile}
          disabled={!selectedFile || loading}
        >
          Limpar
        </button>
      </div>

      {/* Mostrar rotas analisadas após análise */}
      {analiseResultado && analiseResultado.rotas && analiseResultado.rotas.length > 0 && (
        <div className="analise-resultado">
          <h3>Rotas Identificadas</h3>
          <div className="selecao-header">
            <button className="btn-link" onClick={handleSelecionarTodas}>
              {rotasSelecionadas.size === analiseResultado.rotas.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
            </button>
            <span>{rotasSelecionadas.size} de {analiseResultado.rotas.length} selecionadas</span>
          </div>

          <div className="rotas-grid">
            {analiseResultado.rotas.map((rota) => (
              <div
                key={rota.codigo_rota}
                className={`rota-card ${rotasSelecionadas.has(rota.codigo_rota) ? 'selected' : ''}`}
                onClick={() => handleToggleRota(rota.codigo_rota)}
              >
                <div className="rota-header">
                  <div className="rota-codigo">{rota.codigo_rota}</div>
                  {rota.tipo_veiculo && (
                    <div className="rota-veiculo">{rota.tipo_veiculo}</div>
                  )}
                </div>
                <div className="rota-details">
                  <div className="rota-detail-item">
                    <span>📍</span>
                    <span><strong>Origem:</strong> {rota.origem}</span>
                  </div>
                  <div className="rota-detail-item">
                    <span>🏁</span>
                    <span><strong>Destino:</strong> {rota.destino}</span>
                  </div>
                  <div className="rota-detail-item">
                    <span>📏</span>
                    <span><strong>Distância:</strong> {rota.distancia_total_km} km</span>
                  </div>
                </div>
                <div className="rota-checkpoints">
                  {rota.total_checkpoints} checkpoints
                </div>
                <div className="rota-checkbox">
                  <input
                    type="checkbox"
                    checked={rotasSelecionadas.has(rota.codigo_rota)}
                    onChange={() => handleToggleRota(rota.codigo_rota)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="salvar-actions">
            <button
              className="btn btn-primary btn-large"
              onClick={handleSalvarRotas}
              disabled={rotasSelecionadas.size === 0 || loading}
            >
              {loading ? 'Salvando...' : `Salvar ${rotasSelecionadas.size} Rota(s)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderRotasTab = () => {
    // Se está editando uma rota com mapa
    if (editandoRota && rotaEmEdicao) {
      return renderEdicaoRota();
    }
    
    // Se está visualizando um rotograma específico
    if (visualizandoRotograma && rotogramaSelecionado) {
      return renderRotogramaDetalhado();
    }

    // Se ainda não buscou ou não há rotas salvas
    if (loading) {
      return <div className="loading"><div className="loading-spinner"></div><p>Carregando rotas salvas...</p></div>;
    }
    if (!rotasSalvas || rotasSalvas.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <p>Nenhuma rota salva encontrada.</p>
          <p style={{ fontSize: '0.9rem', color: '#999' }}>
            Importe um plano de transporte na aba "Importar" para começar.
          </p>
        </div>
      );
    }
    return (
      <div className="rotas-section">
        <h3>Rotas Salvas</h3>
        <div style={{ marginBottom: '1rem' }}>
          <label>Plano:
            <select value={planoSelecionado ?? ''} onChange={handlePlanoChange} style={{ marginLeft: 8 }}>
              {planos.map((p) => (
                <option key={p.id} value={p.id}>{p.nome}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="rotas-grid">
          {rotasSalvas.map((rota) => (
            <div 
              key={rota.id} 
              className="rota-card clickable"
              onClick={() => handleVisualizarRotograma(rota.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="rota-header">
                <div className="rota-codigo">{rota.codigo_rota}</div>
                {rota.tipo_veiculo && (
                  <div className="rota-veiculo">{rota.tipo_veiculo}</div>
                )}
              </div>
              <div className="rota-details">
                <div className="rota-detail-item">
                  <span>📍</span>
                  <span><strong>Origem:</strong> {rota.origem}</span>
                </div>
                <div className="rota-detail-item">
                  <span>🏁</span>
                  <span><strong>Destino:</strong> {rota.destino}</span>
                </div>
              </div>
              <div className="rota-checkpoints">
                {rota.total_checkpoints ?? '-'} checkpoints
              </div>
              <div className="rota-card-actions" style={{ marginTop: '0.8rem', display: 'flex', gap: '8px' }}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditarRota(rota.id);
                  }}
                  className="btn-action-small"
                  title="Editar rota com mapa"
                >
                  ✏️ Editar
                </button>
                <div style={{ fontSize: '0.85rem', color: '#666', display: 'flex', alignItems: 'center' }}>
                  👁️ Clique para visualizar
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderEdicaoRota = () => {
    if (!rotaEmEdicao) return null;

    return (
      <div className="edicao-rota-container">
        <div className="edicao-header">
          <h3>Editar Rota: {rotaEmEdicao.codigo_rota}</h3>
          <div className="edicao-actions">
            <button onClick={handleCancelarEdicao} className="btn-cancelar">
              ✕ Cancelar
            </button>
            {rotaCalculada && (
              <button onClick={handleSalvarRotaEditada} className="btn-salvar" disabled={loading}>
                ✓ Salvar Alterações
              </button>
            )}
          </div>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="edicao-info">
          <div className="info-item">
            <strong>Origem:</strong> {rotaEmEdicao.origem}
          </div>
          <div className="info-item">
            <strong>Destino:</strong> {rotaEmEdicao.destino}
          </div>
          <div className="info-item">
            <strong>Tipo de Veículo:</strong> {rotaEmEdicao.tipo_veiculo || 'N/A'}
          </div>
          {rotaCalculada && (
            <>
              <div className="info-item">
                <strong>Distância Calculada:</strong> {rotaCalculada.distancia_total_km || rotaCalculada.distancia_total} km
              </div>
              <div className="info-item">
                <strong>Tempo Estimado:</strong> {Math.floor((rotaCalculada.tempo_total_minutos || rotaCalculada.tempo_total || 0) / 60)}h {(rotaCalculada.tempo_total_minutos || rotaCalculada.tempo_total || 0) % 60}min
              </div>
            </>
          )}
        </div>

        <div className="route-editor-content">
          <div className="route-map-section">
            <RouteMap
              checkpoints={[
                ...checkpointsEditados.map(cp => ({
                  ...cp,
                  latitude: cp.latitude ?? (cp.coordenadas ? cp.coordenadas[1] : undefined),
                  longitude: cp.longitude ?? (cp.coordenadas ? cp.coordenadas[0] : undefined),
                })),
                ...(rotaCalculada?.pedagios ? rotaCalculada.pedagios.map((pedagio: any, idx: number) => ({
                  ...pedagio,
                  tipo: 'pedagio',
                  latitude: pedagio.latitude ?? (pedagio.coordenadas ? pedagio.coordenadas[1] : undefined),
                  longitude: pedagio.longitude ?? (pedagio.coordenadas ? pedagio.coordenadas[0] : undefined),
                  nome: pedagio.descricao || `Pedágio ${idx + 1}`,
                  ordem: checkpointsEditados.length + idx + 1,
                })) : [])
              ]}
              routeGeometry={rotaCalculada?.geometry || rotaCalculada?.geometria}
              onCheckpointsChange={(cps) => setCheckpointsEditados(cps as any)}
              editable={true}
              showCalculateButton={true}
              onCalculateRoute={handleCalcularRota}
              calculating={calculandoRota}
            />
          </div>

          {rotaCalculada && rotaCalculada.checkpoints && rotaCalculada.checkpoints.length > 0 && (
            <div className="route-itinerary-section">
              <RouteItinerary 
                checkpoints={rotaCalculada.checkpoints as any} 
                horarioInicio={rotaEmEdicao?.horario_inicio}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRotogramaDetalhado = () => {
    if (!rotogramaSelecionado) return null;

    // Agrupar eventos do itinerário por checkpoint, igual ao print
    let pontosItinerario = rotogramaSelecionado.itinerario_detalhado || [];
    if (!pontosItinerario.length && rotogramaSelecionado.pontos_detalhados) {
      pontosItinerario = rotogramaSelecionado.pontos_detalhados.map((pt: any) => ({
        ...pt,
        descricao: pt.descricao,
        tipo: pt.tipo,
        horario: pt.horario_previsto || pt.horario_chegada || pt.horario_saida || '-'
      }));
    }

    // Função para agrupar eventos extras por checkpoint
    function agruparPorCheckpoint(checkpoints: any[], eventos: any[]) {
      const agrupados: any[] = [];
      let idxEvento = 0;
      for (let i = 0; i < checkpoints.length; i++) {
        const cp = checkpoints[i];
        // Adiciona o checkpoint principal
        agrupados.push({ ...cp, tipo: 'checkpoint', descricao: cp.checkpoint, horario: cp.horario_chegada || cp.horario_previsto || '-' });
        // Adiciona eventos extras entre este checkpoint e o próximo
        const eventosDoCheckpoint = [];
        while (idxEvento < eventos.length && eventos[idxEvento].ordem_checkpoint === cp.ordem) {
          eventosDoCheckpoint.push(eventos[idxEvento]);
          idxEvento++;
        }
        agrupados.push(...eventosDoCheckpoint);
      }
      return agrupados;
    }

    // Se eventos possuem ordem_checkpoint, agrupar por checkpoint
    let itensItinerarioAgrupados = pontosItinerario;
    if (
      Array.isArray(rotogramaSelecionado.checkpoints) &&
      pontosItinerario.length &&
      pontosItinerario.some((ev: any) => ev.ordem_checkpoint !== undefined)
    ) {
      itensItinerarioAgrupados = agruparPorCheckpoint(rotogramaSelecionado.checkpoints, pontosItinerario);
    }
    return (
      <div className="rotograma-detalhado">
        <div className="rotograma-header no-print">
          <button className="btn btn-secondary" onClick={handleVoltarParaLista}>
            ← Voltar para Lista
          </button>
          <button className="btn btn-primary" onClick={handleExportarPDF}>
            📄 Exportar PDF
          </button>
        </div>

        <div className="rotograma-documento">
          {/* Cabeçalho do Rotograma */}
          <div className="rotograma-cabecalho">
            <div className="rotograma-logo">
              <h1>FKS LOGISTICS</h1>
            </div>
            <div className="rotograma-titulo">
              <h2>ROTOGRAMA</h2>
            </div>
          </div>

          {/* Informações principais */}
          <div className="rotograma-info-principal">
            <div className="info-row">
              <div className="info-item">
                <strong>ORIGEM:</strong> {rotogramaSelecionado.origem}
              </div>
              <div className="info-item">
                <strong>DESTINO:</strong> {rotogramaSelecionado.destino}
              </div>
              <div className="info-item">
                <strong>GATE:</strong> <span className="gate-indicator">●●●●●●●●●●</span> <strong>ATRÁS:</strong> <span className="atras-indicator">●●●●</span>
              </div>
            </div>
            <div className="info-row">
              <div className="info-item">
                <strong>LEND.:</strong> {rotogramaSelecionado.codigo_rota}
              </div>
              <div className="info-item">
                <strong>END.:</strong> -
              </div>
              <div className="info-item">
                <strong>LEAD/T:</strong> L1
              </div>
            </div>
            <div className="info-row-dates">
              <div className="info-date">
                <strong>ETD</strong>
                <div className="date-box">{rotogramaSelecionado.etd || '-'}</div>
              </div>
              <div className="info-date">
                <strong>ETA</strong>
                <div className="date-box">{rotogramaSelecionado.eta || '-'}</div>
              </div>
            </div>
          </div>


          {/* Tabela alinhada de Checkpoint e Itinerário - Corrigida */}
          <div className="rotograma-tabelas">
            <table className="tabela-alinhada melhorada rotograma-timeline-table">
              <thead>
                <tr>
                  <th style={{width: '28%'}}>CHECK POINT</th>
                  <th style={{width: '4%'}}></th>
                  <th style={{width: '50%'}}>ITINERÁRIO</th>
                  <th style={{width: '18%'}}>TEMPO</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const checkpoints = rotogramaSelecionado.checkpoints || [];
                  const itinerario = itensItinerarioAgrupados || [];
                  const linhas = [];
                  let itIdx = 0;
                  for (let cpIdx = 0; cpIdx < checkpoints.length; cpIdx++) {
                    const cp = checkpoints[cpIdx];
                    let it = itinerario[itIdx];
                    if (it && it.tipo === 'checkpoint') {
                      linhas.push(
                        <tr key={cpIdx} style={{height: '38px'}}>
                          <td style={{fontWeight: 700, background: '#f7f7f7'}}>{cp.checkpoint || cp.nome || cp.descricao || '-'}</td>
                          <td className="timeline-cell">
                            <div className="timeline-dot" style={{background: '#2196F3'}}></div>
                            {cpIdx < checkpoints.length - 1 && <div className="timeline-line"></div>}
                          </td>
                          <td style={{fontWeight: 700, background: '#f7f7f7'}}>{it.descricao || '-'}</td>
                          <td style={{fontWeight: 700, background: '#f7f7f7', textAlign: 'center'}}>{it.horario ? it.horario : '-'}</td>
                        </tr>
                      );
                      itIdx++;
                    }
                    while (itIdx < itinerario.length && itinerario[itIdx].tipo !== 'checkpoint') {
                      const extra = itinerario[itIdx];
                      linhas.push(
                        <tr key={cpIdx + '-evt-' + itIdx} style={{height: '32px'}}>
                          <td></td>
                          <td className="timeline-cell">{cpIdx < checkpoints.length - 1 && <div className="timeline-line"></div>}</td>
                          <td style={{color: '#333'}}>{extra.descricao}</td>
                          <td style={{textAlign: 'center'}}>{extra.horario ? extra.horario : '-'}</td>
                        </tr>
                      );
                      itIdx++;
                    }
                  }
                  return linhas;
                })()}
              </tbody>
            </table>
          </div>

          {/* Rodapé com informações adicionais */}
          <div className="rotograma-rodape">
            <div className="info-adicional">
              <strong>Total de Checkpoints:</strong> {rotogramaSelecionado.total_checkpoints}
            </div>
            <div className="info-adicional">
              <strong>Distância Total:</strong> {rotogramaSelecionado.distancia_total_km} km
            </div>
            <div className="info-adicional">
              <strong>Tipo de Veículo:</strong> {rotogramaSelecionado.tipo_veiculo || 'N/A'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderHistoricoTab = () => (
    <div className="empty-state">
      <div className="empty-state-icon">📊</div>
      <p>Histórico de rotogramas em desenvolvimento...</p>
    </div>
  );

  return (
    <div className="rotograma-module">
      <h2>Módulo Rotograma</h2>

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          📤 Importar
        </button>
        <button
          className={`tab-button ${activeTab === 'rotas' ? 'active' : ''}`}
          onClick={() => setActiveTab('rotas')}
        >
          🗺️ Rotas Salvas
        </button>
        <button
          className={`tab-button ${activeTab === 'historico' ? 'active' : ''}`}
          onClick={() => setActiveTab('historico')}
        >
          📋 Histórico
        </button>
      </div>

      {loading && (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Processando...</p>
        </div>
      )}

      {!loading && (
        <>
          {activeTab === 'upload' && renderUploadTab()}
          {activeTab === 'rotas' && renderRotasTab()}
          {activeTab === 'historico' && renderHistoricoTab()}
        </>
      )}
    </div>
  );
};

export default RotogramaModule;
